import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SCRIPT_TOKENS } from '../lib/scriptTokens';

// ---------------------------------------------------------------------------
// Textfeld fürs Skript-Schreiben: tippt jemand `[`, öffnet sich die Liste der Bausteine, die im
// Call automatisch gefüllt werden.
//
// Der Anlass: die Bausteine gab es längst ([name], [firmenname], [job] …), aber nur, wenn man sie
// auswendig wusste. Im Block-Editor standen sie als Knopfreihe über dem Feld — beim Einstellen
// eines ganzen Skripts (Skript-Bibliothek) gab es gar keinen Hinweis, und ein falsch geschriebener
// Baustein fällt erst im Gespräch auf, weil dann eckige Klammern vorgelesen werden. Die eckige
// Klammer ist ohnehin das Zeichen, das man tippt — sie ist deshalb der richtige Auslöser.
//
// Bewusst UNTER dem Feld verankert und nicht am Schreibcursor: die Cursor-Position in einem
// <textarea> lässt sich nur über eine unsichtbare Zwillings-Ebene schätzen (Spiegel-Div), die bei
// jedem Zeilenumbruch, jeder Schriftgrösse und jedem Scrollstand neu danebenliegen kann. Eine
// Liste, die verlässlich an derselben Stelle steht, ist im Schreibfluss mehr wert als eine, die
// meistens am Cursor klebt.
// ---------------------------------------------------------------------------

// Das offene `[` samt bereits getipptem Filter — aber nur, wenn seither KEINE schliessende Klammer
// kam. `[name] und dann` darf die Liste nicht wieder aufmachen.
function offenerBaustein(text: string, caret: number): { start: number; filter: string } | null {
  const davor = text.slice(0, caret);
  const start = davor.lastIndexOf('[');
  if (start === -1) return null;
  const filter = davor.slice(start + 1);
  if (filter.includes(']') || filter.includes('\n')) return null;
  return { start, filter };
}

export function TokenTextarea({
  value,
  onChange,
  className = '',
  rows = 10,
  placeholder,
  autoFocus,
  taRef,
}: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  rows?: number;
  placeholder?: string;
  autoFocus?: boolean;
  taRef?: React.RefObject<HTMLTextAreaElement | null>;
}) {
  const eigenerRef = useRef<HTMLTextAreaElement>(null);
  const ref = taRef ?? eigenerRef;
  const [offen, setOffen] = useState<{ start: number; filter: string } | null>(null);
  const [idx, setIdx] = useState(0);
  // Position des Dropdowns im Viewport — siehe Portal-Kommentar weiter unten.
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);

  // Wächst mit dem Inhalt statt zu scrollen — vorher `rows` fest und ein `resize-y`-Griff, den
  // niemand fand. `rows` bleibt als anfängliche Mindesthöhe stehen, die Höhe selbst übernimmt ab
  // hier `scrollHeight`. Kein Deckel: die umgebenden Flächen sind ohnehin scrollbar.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Der Dropdown rendert seit hier über ein Portal nach `document.body` (siehe unten) — er sitzt
  // damit ausserhalb jeder scrollbaren/`overflow-hidden`-Spalte, die ihn sonst anschneidet. Seine
  // Position wird deshalb hier aus der tatsächlichen Bildschirmposition des Textfelds berechnet,
  // nicht mehr über `absolute`/`top-full` relativ zum Feld.
  useLayoutEffect(() => {
    if (!offen) return;
    const el = ref.current;
    if (!el) return;
    const berechne = () => {
      const r = el.getBoundingClientRect();
      const platzUnten = window.innerHeight - r.bottom;
      // Nach oben klappen, wenn unten zu wenig Platz ist UND oben mehr ist — sonst lieber unten
      // bleiben (die Liste ist ohnehin selbst scrollbar).
      const nachOben = platzUnten < 240 && r.top > platzUnten;
      setPos(nachOben
        ? { left: r.left, width: r.width, bottom: window.innerHeight - r.top + 4 }
        : { left: r.left, width: r.width, top: r.bottom + 4 });
    };
    berechne();
    // Capture-Phase: erfasst auch das Scrollen eines verschachtelten Vorfahren, das sonst nie bis
    // zum `window`-Listener durchbubbelt.
    window.addEventListener('scroll', berechne, true);
    window.addEventListener('resize', berechne);
    return () => {
      window.removeEventListener('scroll', berechne, true);
      window.removeEventListener('resize', berechne);
    };
  }, [offen]);

  const treffer = useMemo(() => {
    if (!offen) return [];
    const q = offen.filter.trim().toLowerCase();
    const passt = SCRIPT_TOKENS.filter(
      (t) => !q || t.key.includes(q) || t.label.toLowerCase().includes(q),
    );
    return passt.slice(0, 8);
  }, [offen]);

  useEffect(() => { setIdx(0); }, [offen?.filter]);

  const pruefen = () => {
    const el = ref.current;
    if (!el) return;
    setOffen(offenerBaustein(el.value, el.selectionStart ?? el.value.length));
  };

  // Den Baustein an die Stelle des offenen `[` setzen — die halb getippte Eingabe wird ersetzt,
  // nicht ergänzt, sonst stünde da `[nam[name]`.
  const einsetzen = (key: string) => {
    const el = ref.current;
    if (!el || !offen) return;
    const caret = el.selectionStart ?? value.length;
    const neu = `${value.slice(0, offen.start)}[${key}]${value.slice(caret)}`;
    onChange(neu);
    setOffen(null);
    const p = offen.start + key.length + 2;
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(p, p); });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!offen || treffer.length === 0) return;
    // Nur diese fünf Tasten gehören der Liste. Alles andere — auch Enter, wenn die Liste zu ist —
    // bleibt beim Schreiben, sonst kostet der Baustein-Helfer den Zeilenumbruch.
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => (i + 1) % treffer.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => (i - 1 + treffer.length) % treffer.length); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); einsetzen(treffer[idx].key); }
    else if (e.key === 'Escape') { e.preventDefault(); setOffen(null); }
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        rows={rows}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => { onChange(e.target.value); requestAnimationFrame(pruefen); }}
        onKeyUp={pruefen}
        onClick={pruefen}
        // Nicht sofort schliessen: ein Klick in die Liste nimmt dem Feld zuerst den Fokus.
        onBlur={() => setTimeout(() => setOffen(null), 150)}
        onKeyDown={onKeyDown}
        className={className}
      />
      {offen && treffer.length > 0 && pos && createPortal(
        <div
          style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}
          className="z-30 max-h-56 overflow-y-auto rounded-lg border border-accent/40 bg-bg-surface shadow-xl"
        >
          <div className="px-2.5 py-1.5 text-[10px] uppercase tracking-wider text-text-muted border-b border-border-primary">
            Baustein einsetzen · <span className="text-text-main">↑↓</span> wählen ·{' '}
            <span className="text-text-main">Enter</span> einsetzen · füllt sich beim Anruf
          </div>
          {treffer.map((t, i) => (
            <button
              key={t.key}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => einsetzen(t.key)}
              onMouseEnter={() => setIdx(i)}
              className={`w-full text-left px-2.5 py-1.5 flex items-baseline gap-2 ${
                i === idx ? 'bg-accent/10' : 'hover:bg-bg-elevated'
              }`}
            >
              <span className={`font-mono text-[12px] shrink-0 ${i === idx ? 'text-accent' : 'text-text-main'}`}>
                [{t.key}]
              </span>
              <span className="text-[11px] text-text-muted truncate">{t.hinweis}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
